import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AtcFlowKind, AtcScreen } from "~/features/addToCart/flow/types";

export type CloseIntent = "close" | "closeAndRestorePrevious";

export type AtcSubmittableScreen = Extract<AtcScreen, "kitSummary" | "defaultForm">;

type ContractScreenDecision = "none" | "continueWithoutContract" | "contractLoadErrored";

export interface UseAtcFlowControllerParams<TSubmitData> {
  flowKind: AtcFlowKind;
  resolvedScreen: AtcScreen;
  resolvedInteractiveScreen: AtcScreen;
  prepareSubmitData: () => TSubmitData;
  onSubmit: (data: TSubmitData) => Promise<boolean>;
  onRequestClose: (intent: CloseIntent) => void;
  autoSubmitKey?: string | null;
  getCloseIntent?: (params: {
    source: "close" | "cancel" | "submitSuccess";
    screen: AtcScreen;
    flowKind: AtcFlowKind;
  }) => CloseIntent;
}

export interface UseAtcFlowControllerResult {
  currentScreen: AtcScreen;
  isSending: boolean;
  continueWithoutContract: () => void;
  markContractLoadErrored: () => void;
  requestClose: (intent?: CloseIntent) => void;
  submit: () => void;
}

const SUBMITTABLE_SCREENS: readonly AtcSubmittableScreen[] = ["kitSummary", "defaultForm"];

export function isSubmittableAtcScreen(screen: AtcScreen): screen is AtcSubmittableScreen {
  return (SUBMITTABLE_SCREENS as readonly AtcScreen[]).includes(screen);
}

export function getAtcAutoSubmitKey({
  flowKind,
  screen,
  quantityVisible,
}: {
  flowKind: AtcFlowKind;
  screen: AtcScreen;
  quantityVisible: boolean;
}) {
  if (quantityVisible) {
    return null;
  }

  if (flowKind === "addToCart" && (screen === "defaultForm" || screen === "kitSummary")) {
    return `${flowKind}:${screen}:hidden-quantity`;
  }

  if (flowKind === "editPart" && screen === "defaultForm") {
    return `${flowKind}:${screen}:hidden-quantity`;
  }

  return null;
}

export function getDefaultCloseIntent({
  source,
  screen,
  flowKind,
}: {
  source: "close" | "cancel" | "submitSuccess";
  screen: AtcScreen;
  flowKind: AtcFlowKind;
}): CloseIntent {
  if (source !== "submitSuccess" && flowKind === "addToCart" && screen === "kitSummary") {
    return "closeAndRestorePrevious";
  }

  return "close";
}

export function shouldIgnoreAtcAsyncResult({ isMounted, isClosed }: { isMounted: boolean; isClosed: boolean }) {
  return !isMounted || isClosed;
}

function resolveCurrentScreen({
  resolvedScreen,
  resolvedInteractiveScreen,
  contractScreenDecision,
  submitFailed,
}: {
  resolvedScreen: AtcScreen;
  resolvedInteractiveScreen: AtcScreen;
  contractScreenDecision: ContractScreenDecision;
  submitFailed: boolean;
}) {
  let nextScreen = resolvedScreen;

  if (resolvedScreen === "contractAvailable") {
    if (contractScreenDecision === "continueWithoutContract") {
      nextScreen = resolvedInteractiveScreen;
    } else if (contractScreenDecision === "contractLoadErrored") {
      nextScreen = "contractLoadErrored";
    }
  }

  if (submitFailed) {
    return "errorSending";
  }

  return nextScreen;
}

export function useAtcFlowController<TSubmitData>({
  flowKind,
  resolvedScreen,
  resolvedInteractiveScreen,
  prepareSubmitData,
  onSubmit,
  onRequestClose,
  autoSubmitKey,
  getCloseIntent = getDefaultCloseIntent,
}: UseAtcFlowControllerParams<TSubmitData>): UseAtcFlowControllerResult {
  const [isSending, setIsSending] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [contractScreenDecision, setContractScreenDecision] = useState<ContractScreenDecision>("none");

  const submitLockRef = useRef(false);
  const isMountedRef = useRef(true);
  const isClosedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      isClosedRef.current = true;
      submitLockRef.current = false;
    };
  }, []);

  const currentScreen = useMemo(
    () =>
      resolveCurrentScreen({
        resolvedScreen,
        resolvedInteractiveScreen,
        contractScreenDecision,
        submitFailed,
      }),
    [contractScreenDecision, resolvedInteractiveScreen, resolvedScreen, submitFailed],
  );

  const requestClose = useCallback(
    (intent?: CloseIntent) => {
      const nextIntent = intent ?? getCloseIntent({ source: "close", screen: currentScreen, flowKind });
      submitLockRef.current = false;
      isClosedRef.current = true;
      setIsSending(false);
      setSubmitFailed(false);
      onRequestClose(nextIntent);
    },
    [currentScreen, flowKind, getCloseIntent, onRequestClose],
  );

  const executeSubmit = useCallback(() => {
    if (!isSubmittableAtcScreen(currentScreen) || isSending || submitLockRef.current) {
      return false;
    }

    submitLockRef.current = true;
    setIsSending(true);
    setSubmitFailed(false);

    const submitScreen = currentScreen;

    void Promise.resolve()
      .then(() => onSubmit(prepareSubmitData()))
      .then((result) => {
        if (
          shouldIgnoreAtcAsyncResult({
            isMounted: isMountedRef.current,
            isClosed: isClosedRef.current,
          })
        ) {
          return;
        }

        if (result) {
          isClosedRef.current = true;
          setIsSending(false);
          setSubmitFailed(false);
          onRequestClose(getCloseIntent({ source: "submitSuccess", screen: submitScreen, flowKind }));
          return;
        }

        submitLockRef.current = false;
        setIsSending(false);
        setSubmitFailed(true);
      })
      .catch(() => {
        if (
          shouldIgnoreAtcAsyncResult({
            isMounted: isMountedRef.current,
            isClosed: isClosedRef.current,
          })
        ) {
          return;
        }

        submitLockRef.current = false;
        setIsSending(false);
        setSubmitFailed(true);
      });

    return true;
  }, [currentScreen, flowKind, getCloseIntent, isSending, onRequestClose, onSubmit, prepareSubmitData]);

  const continueWithoutContract = useCallback(() => {
    if (resolvedScreen !== "contractAvailable" || isSending) {
      return;
    }

    setContractScreenDecision("continueWithoutContract");
  }, [isSending, resolvedScreen]);

  const markContractLoadErrored = useCallback(() => {
    if (resolvedScreen !== "contractAvailable" || isSending) {
      return;
    }

    setContractScreenDecision("contractLoadErrored");
  }, [isSending, resolvedScreen]);

  useEffect(() => {
    if (!autoSubmitKey || isSending || !isSubmittableAtcScreen(currentScreen)) {
      return;
    }

    const didStartSubmit = executeSubmit();
    if (!didStartSubmit) {
      return;
    }
  }, [autoSubmitKey, currentScreen, executeSubmit, isSending]);

  const submit = useCallback(() => {
    executeSubmit();
  }, [executeSubmit]);

  return {
    currentScreen,
    isSending,
    continueWithoutContract,
    markContractLoadErrored,
    requestClose,
    submit,
  };
}
